import React, {
    useMemo,
    useState,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import { Popover, Input, Tag, Icon } from 'antd';
import classname from 'classname';
import regionDataSource from './data.json';
import './index.scss';

const MUNICIPALITIES = new Set(['北京', '上海', '天津', '重庆']);
const SEARCH_DEBOUNCE = 300;

export interface IRegionRecord {
    province: string;
    cities?: Array<{
        city: string;
    }>;
}

export type RegionSelectorValue = string[]; // 返回城市或省份（全选省份时返回省份）

export interface IRegionSelectorOption {
    key: string;
    label: string;
    province: string;
    city: string;
    type: 'city';
}

export interface IProvinceMeta {
    province: string;
    isMunicipality: boolean;
    cities: string[];
}

export interface IRegionSelectorProps {
    value?: RegionSelectorValue;
    defaultValue?: RegionSelectorValue;
    onChange?: (
        cities: RegionSelectorValue,
        options: IRegionSelectorOption[],
    ) => void;
    onReachMax?: (max: number) => void;
    maxSelection?: number;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    style?: React.CSSProperties;
    dataSource?: IRegionRecord[];
    maxTagCount?: number;
}

const sanitizeList = (list: unknown[]): string[] =>
    Array.from(
        new Set(
            list
                .map(item => {
                    if (typeof item === 'string') {
                        return item.trim();
                    }
                    if (typeof item === 'number') {
                        return String(item).trim();
                    }
                    return '';
                })
                .filter(Boolean),
        ),
    );

const normalizeRecords = (records: IRegionRecord[]): IProvinceMeta[] =>
    records.map(record => {
        const province = record.province;
        const isMunicipality = MUNICIPALITIES.has(province);
        const cities = isMunicipality
            ? []
            : (record.cities || [])
                  .map(item => item.city)
                  .filter(Boolean)
                  .map(city => city.trim());
        return {
            province,
            isMunicipality,
            cities,
        };
    });

type FlatOption =
    | {
          key: string;
          label: string;
          province: string;
          type: 'province';
      }
    | (IRegionSelectorOption & { type: 'city' });

const buildRegionIndex = (provinces: IProvinceMeta[]) => {
    const provinceCityNameMap: Record<string, string[]> = {};
    const provinceCityKeyMap: Record<string, string[]> = {};
    const cityOptionMap: Record<string, IRegionSelectorOption> = {};
    const cityNameToKeysMap = new Map<string, string[]>();
    const provinceOptions: Record<string, { key: string; label: string }> = {};
    const flatOptions: FlatOption[] = [];

    provinces.forEach(provinceMeta => {
        const { province, isMunicipality, cities } = provinceMeta;
        provinceOptions[province] = { key: province, label: province };
        flatOptions.push({
            key: province,
            label: province,
            type: 'province',
            province,
        });

        if (isMunicipality) {
            const cityKey = province;
            const cityOption: IRegionSelectorOption = {
                key: cityKey,
                label: province,
                province,
                city: province,
                type: 'city',
            };
            cityOptionMap[cityKey] = cityOption;
            provinceCityNameMap[province] = [province];
            provinceCityKeyMap[province] = [cityKey];
            cityNameToKeysMap.set(province, [cityKey]);
            flatOptions.push(cityOption);
        } else {
            provinceCityNameMap[province] = cities;
            provinceCityKeyMap[province] = [];
            cities.forEach(city => {
                const cityKey = `${province}/${city}`;
                const cityOption: IRegionSelectorOption = {
                    key: cityKey,
                    label: `${province} - ${city}`,
                    province,
                    city,
                    type: 'city',
                };
                cityOptionMap[cityKey] = cityOption;
                provinceCityKeyMap[province].push(cityKey);
                flatOptions.push(cityOption);
                const existing = cityNameToKeysMap.get(city) || [];
                existing.push(cityKey);
                cityNameToKeysMap.set(city, existing);
            });
        }
    });

    return {
        provinceCityNameMap,
        provinceCityKeyMap,
        cityOptionMap,
        cityNameToKeysMap,
        provinceOptions,
        flatOptions,
    };
};

const getInitialProvince = (provinces: IProvinceMeta[]): string =>
    provinces.length ? provinces[0].province : '';

export const RegionSelector: React.FC<IRegionSelectorProps> = ({
    value,
    defaultValue = [],
    onChange,
    onReachMax,
    maxSelection = 0,
    placeholder = '请选择城市地区',
    disabled = false,
    className,
    style,
    dataSource = regionDataSource as IRegionRecord[],
    maxTagCount,
}) => {
    const provinces = useMemo(() => normalizeRecords(dataSource), [dataSource]);

    const {
        provinceCityNameMap,
        provinceCityKeyMap,
        cityOptionMap,
        cityNameToKeysMap,
        provinceOptions,
        flatOptions,
    } = useMemo(() => buildRegionIndex(provinces), [provinces]);

    const deriveKeysFromCities = useCallback(
        (cities: RegionSelectorValue): Set<string> => {
            const cityNameSet = new Set(sanitizeList(cities));
            const keys = new Set<string>();

            provinces.forEach(({ province, isMunicipality }) => {
                if (cityNameSet.has(province)) {
                    keys.add(province);
                    cityNameSet.delete(province);
                    return;
                }
                const cityNames = isMunicipality
                    ? [province]
                    : provinceCityNameMap[province] || [];
                if (cityNames.length === 0) {
                    return;
                }
                const allIncluded = cityNames.every(name =>
                    cityNameSet.has(name),
                );
                if (allIncluded) {
                    keys.add(province);
                    cityNames.forEach(name => cityNameSet.delete(name));
                }
            });

            cityNameSet.forEach(cityName => {
                const candidates = cityNameToKeysMap.get(cityName);
                if (candidates && candidates.length) {
                    keys.add(candidates[0]);
                }
            });

            return keys;
        },
        [provinces, provinceCityNameMap, cityNameToKeysMap],
    );

    const convertKeysToValues = useCallback(
        (keys: Set<string>): RegionSelectorValue => {
            const seen = new Set<string>();
            const list: string[] = [];
            const pushValue = (value: string) => {
                if (!seen.has(value)) {
                    seen.add(value);
                    list.push(value);
                }
            };
            keys.forEach(key => {
                if (provinceOptions[key]) {
                    pushValue(key);
                } else if (cityOptionMap[key]) {
                    pushValue(cityOptionMap[key].city);
                }
            });
            return list;
        },
        [provinceOptions, cityOptionMap],
    );

    const convertKeysToCityOptions = useCallback(
        (keys: Set<string>): IRegionSelectorOption[] => {
            const options: IRegionSelectorOption[] = [];
            const pushed = new Set<string>();
            keys.forEach(key => {
                if (provinceOptions[key]) {
                    const cityKeys = provinceCityKeyMap[key];
                    if (cityKeys && cityKeys.length) {
                        cityKeys.forEach(cityKey => {
                            const option = cityOptionMap[cityKey];
                            if (option && !pushed.has(cityKey)) {
                                options.push(option);
                                pushed.add(cityKey);
                            }
                        });
                    } else {
                        if (!pushed.has(key)) {
                            options.push({
                                key,
                                label: key,
                                province: key,
                                city: key,
                                type: 'city',
                            });
                            pushed.add(key);
                        }
                    }
                } else {
                    const option = cityOptionMap[key];
                    if (option && !pushed.has(key)) {
                        options.push(option);
                        pushed.add(key);
                    }
                }
            });
            return options;
        },
        [provinceOptions, provinceCityKeyMap, cityOptionMap],
    );

    const isControlled = typeof value !== 'undefined';
    const [innerSelectedKeys, setInnerSelectedKeys] = useState<Set<string>>(
        () => deriveKeysFromCities(defaultValue),
    );

    useEffect(() => {
        if (!isControlled) {
            setInnerSelectedKeys(deriveKeysFromCities(defaultValue));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);

    const selectedKeys = useMemo(() => {
        if (isControlled) {
            return deriveKeysFromCities(value || []);
        }
        return innerSelectedKeys;
    }, [isControlled, value, innerSelectedKeys, deriveKeysFromCities]);

    const selectedCities = useMemo(() => convertKeysToValues(selectedKeys), [
        selectedKeys,
        convertKeysToValues,
    ]);

    const [visible, setVisible] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [activeProvince, setActiveProvince] = useState<string>(() =>
        getInitialProvince(provinces),
    );
    const searchInputRef = useRef<Input | null>(null);
    const debounceTimerRef = useRef<number | null>(null);

    useEffect(() => {
        if (!activeProvince) {
            return;
        }
        const exists = provinces.some(item => item.province === activeProvince);
        if (!exists) {
            setActiveProvince(getInitialProvince(provinces));
        }
    }, [activeProvince, provinces]);

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                window.clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const applySelection = useCallback(
        (nextKeys: Set<string>) => {
            const clonedKeys = new Set(nextKeys);
            if (maxSelection > 0) {
                const selectedCount = clonedKeys.size;
                if (selectedCount > maxSelection) {
                    if (onReachMax) {
                        onReachMax(maxSelection);
                    }
                    return;
                }
            }
            if (!isControlled) {
                setInnerSelectedKeys(clonedKeys);
            }
            if (onChange) {
                const cities = convertKeysToValues(clonedKeys);
                const options = convertKeysToCityOptions(clonedKeys);
                onChange(cities, options);
            }
        },
        [
            isControlled,
            maxSelection,
            onChange,
            onReachMax,
            convertKeysToCityOptions,
            convertKeysToValues,
        ],
    );

    const toggleProvince = useCallback(
        (province: string) => {
            if (disabled) {
                return;
            }
            const nextKeys = new Set(selectedKeys);
            if (nextKeys.has(province)) {
                nextKeys.delete(province);
            } else {
                const cityKeys = provinceCityKeyMap[province] || [];
                cityKeys.forEach(cityKey => nextKeys.delete(cityKey));
                nextKeys.add(province);
            }
            applySelection(nextKeys);
        },
        [disabled, selectedKeys, provinceCityKeyMap, applySelection],
    );

    const toggleCity = useCallback(
        (province: string, city: string) => {
            if (disabled) {
                return;
            }
            const nextKeys = new Set(selectedKeys);
            const provinceKey = province;
            const availableKeys = provinceCityKeyMap[province] || [];
            let cityKey =
                availableKeys.find(key => key.endsWith(`/${city}`)) || null;
            if (!cityKey) {
                cityKey =
                    availableKeys.find(
                        key => key === city || key === province,
                    ) || `${province}/${city}`;
            }

            if (nextKeys.has(provinceKey)) {
                nextKeys.delete(provinceKey);
            }
            if (nextKeys.has(cityKey)) {
                nextKeys.delete(cityKey);
            } else {
                nextKeys.add(cityKey);
            }
            applySelection(nextKeys);
        },
        [disabled, selectedKeys, provinceCityKeyMap, applySelection],
    );

    const clearAll = useCallback(() => {
        applySelection(new Set<string>());
    }, [applySelection]);

    const renderTags = () => {
        if (!selectedCities.length) {
            return (
                <span className="crm-region-selector-placeholder">
                    {placeholder}
                </span>
            );
        }
        const list =
            typeof maxTagCount === 'number' && maxTagCount >= 0
                ? selectedCities.slice(0, maxTagCount)
                : selectedCities;
        return (
            <>
                {list.map(city => (
                    <Tag
                        key={city}
                        closable={!disabled}
                        onClose={(event: React.MouseEvent<HTMLElement>) => {
                            event.preventDefault();
                            const nextKeys = new Set(selectedKeys);
                            if (provinceOptions[city]) {
                                nextKeys.delete(city);
                                applySelection(nextKeys);
                                return;
                            }
                            // remove this city
                            cityNameToKeysMap
                                .get(city)
                                ?.forEach(cityKey => nextKeys.delete(cityKey));
                            // also check if province includes this city
                            provinces.forEach(({ province }) => {
                                if (nextKeys.has(province)) {
                                    const cityNames =
                                        provinceCityNameMap[province] || [];
                                    if (cityNames.includes(city)) {
                                        nextKeys.delete(province);
                                    }
                                }
                            });
                            applySelection(nextKeys);
                        }}
                    >
                        {city}
                    </Tag>
                ))}
                {selectedCities.length > list.length ? (
                    <span className="crm-region-selector-more">
                        +{selectedCities.length - list.length}
                    </span>
                ) : null}
            </>
        );
    };

    const searchResult = useMemo(() => {
        if (!keyword.trim()) {
            return [];
        }
        const lower = keyword.trim().toLowerCase();
        return flatOptions.filter(option =>
            option.label.toLowerCase().includes(lower),
        );
    }, [flatOptions, keyword]);

    const renderCityCheckbox = (province: string, city: string) => {
        const provinceKey = province;
        const cityKey =
            provinceCityKeyMap[province]?.find(key =>
                key.endsWith(`/${city}`),
            ) || `${province}/${city}`;
        const provinceSelected = selectedKeys.has(provinceKey);
        const checked = provinceSelected || selectedKeys.has(cityKey);
        const disabledCity = disabled || provinceSelected;
        return (
            <div
                key={cityKey}
                className={classname('crm-region-selector-tag', {
                    selected: checked,
                    disabled: disabledCity,
                })}
                onClick={() => toggleCity(province, city)}
            >
                <span className="text">{city}</span>
                {checked ? <Icon type="check" /> : null}
            </div>
        );
    };

    const renderProvincePanel = () => (
        <div className="crm-region-selector-panel">
            <div className="crm-region-selector-panel-left">
                {provinces.map(item => {
                    const isActive = activeProvince === item.province;
                    const provinceSelected = selectedKeys.has(item.province);
                    const cityKeys = provinceCityKeyMap[item.province] || [];
                    const selectedCitiesCount = cityKeys.filter(cityKey =>
                        selectedKeys.has(cityKey),
                    ).length;
                    const hasPartial =
                        selectedCitiesCount > 0 &&
                        selectedCitiesCount < cityKeys.length;
                    return (
                        <div
                            key={item.province}
                            className={classname(
                                'crm-region-selector-province',
                                {
                                    active: isActive,
                                    disabled,
                                    selected: provinceSelected,
                                    indeterminate: hasPartial,
                                },
                            )}
                            onClick={() => {
                                if (!disabled) {
                                    setActiveProvince(item.province);
                                }
                            }}
                        >
                            <span className="crm-region-selector-province-text">
                                {item.province}
                            </span>
                            <span className="crm-region-selector-province-status">
                                {provinceSelected ? (
                                    <Icon type="check" />
                                ) : hasPartial ? (
                                    <Icon type="minus" />
                                ) : (
                                    <Icon type="right" />
                                )}
                            </span>
                        </div>
                    );
                })}
            </div>
            <div className="crm-region-selector-panel-right">
                {(() => {
                    const current = provinces.find(
                        item => item.province === activeProvince,
                    );
                    if (!current) {
                        return (
                            <div className="crm-region-selector-empty">
                                请选择省份
                            </div>
                        );
                    }
                    if (current.isMunicipality) {
                        const selected = selectedKeys.has(current.province);
                        return (
                            <div className="crm-region-selector-cascade">
                                <div
                                    className={classname(
                                        'crm-region-selector-item',
                                        {
                                            selected,
                                            disabled,
                                        },
                                    )}
                                    onClick={() =>
                                        toggleProvince(current.province)
                                    }
                                >
                                    <span className="text">
                                        {current.province}
                                    </span>
                                    {selected ? <Icon type="check" /> : null}
                                </div>
                                <div className="crm-region-selector-tip">
                                    该地区仅提供市级选项
                                </div>
                            </div>
                        );
                    }
                    const provinceSelected = selectedKeys.has(current.province);
                    const cityKeys = provinceCityKeyMap[current.province] || [];
                    const selectedCitiesCount = cityKeys.filter(cityKey =>
                        selectedKeys.has(cityKey),
                    ).length;
                    const allSelected =
                        cityKeys.length > 0 &&
                        selectedCitiesCount === cityKeys.length;
                    return (
                        <div className="crm-region-selector-cascade">
                            <div
                                className={classname(
                                    'crm-region-selector-item',
                                    'check-all',
                                    {
                                        selected:
                                            provinceSelected || allSelected,
                                        disabled,
                                    },
                                )}
                                onClick={() => toggleProvince(current.province)}
                            >
                                <span className="text">全部</span>
                                {provinceSelected || allSelected ? (
                                    <Icon type="check" />
                                ) : null}
                            </div>
                            <div className="crm-region-selector-cities">
                                {(
                                    provinceCityNameMap[current.province] || []
                                ).map(city =>
                                    renderCityCheckbox(current.province, city),
                                )}
                            </div>
                        </div>
                    );
                })()}
            </div>
        </div>
    );

    const renderSearchPanel = () => {
        if (searchResult.length === 0) {
            return (
                <div className="crm-region-selector-empty">
                    未找到“{searchValue}”相关地区
                </div>
            );
        }
        return (
            <div className="crm-region-selector-search-result">
                {searchResult.map(option => {
                    const provinceSelected = selectedKeys.has(option.province);
                    const selected =
                        option.type === 'province'
                            ? provinceSelected
                            : provinceSelected || selectedKeys.has(option.key);
                    return (
                        <div
                            key={option.key}
                            className={classname(
                                'crm-region-selector-search-item',
                                {
                                    selected,
                                    disabled,
                                },
                            )}
                            onClick={() => {
                                if (option.type === 'province') {
                                    toggleProvince(option.province);
                                    setActiveProvince(option.province);
                                } else if (option.city) {
                                    const provinceKeys =
                                        provinceCityKeyMap[option.province] ||
                                        [];
                                    const isMunicipalityMatch =
                                        provinceKeys.length === 1 &&
                                        provinceKeys[0] === option.province;
                                    if (isMunicipalityMatch) {
                                        toggleProvince(option.province);
                                        setActiveProvince(option.province);
                                    } else {
                                        toggleCity(
                                            option.province,
                                            option.city,
                                        );
                                        setActiveProvince(option.province);
                                    }
                                }
                            }}
                        >
                            <span className="text">
                                {option.type === 'province'
                                    ? option.province
                                    : `${option.province} - ${option.city}`}
                            </span>
                            {selected ? <Icon type="check" /> : null}
                        </div>
                    );
                })}
            </div>
        );
    };

    const overlayContent = (
        <div
            className="crm-region-selector-dropdown"
            onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                event.stopPropagation()
            }
        >
            <div className="crm-region-selector-search">
                <Input
                    ref={searchInputRef}
                    value={searchValue}
                    placeholder="请输入城市地区"
                    allowClear
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const nextValue = event.target.value;
                        setSearchValue(nextValue);
                        if (debounceTimerRef.current) {
                            window.clearTimeout(debounceTimerRef.current);
                        }
                        debounceTimerRef.current = window.setTimeout(() => {
                            setKeyword(nextValue);
                        }, SEARCH_DEBOUNCE);
                    }}
                />
                <div className="crm-region-selector-selected">
                    {selectedCities.length
                        ? selectedCities.map(city => (
                              <Tag
                                  key={city}
                                  closable={!disabled}
                                  onClose={(
                                      event: React.MouseEvent<HTMLElement>,
                                  ) => {
                                      event.preventDefault();
                                      const nextKeys = new Set(selectedKeys);
                                      if (provinceOptions[city]) {
                                          nextKeys.delete(city);
                                          applySelection(nextKeys);
                                          return;
                                      }
                                      cityNameToKeysMap
                                          .get(city)
                                          ?.forEach(cityKey =>
                                              nextKeys.delete(cityKey),
                                          );
                                      provinces.forEach(({ province }) => {
                                          if (nextKeys.has(province)) {
                                              const cityNames =
                                                  provinceCityNameMap[
                                                      province
                                                  ] || [];
                                              if (cityNames.includes(city)) {
                                                  nextKeys.delete(province);
                                              }
                                          }
                                      });
                                      applySelection(nextKeys);
                                  }}
                              >
                                  {city}
                              </Tag>
                          ))
                        : null}
                </div>
            </div>
            {keyword.trim() ? renderSearchPanel() : renderProvincePanel()}
        </div>
    );

    useEffect(() => {
        if (visible) {
            searchInputRef.current?.focus();
        }
    }, [visible]);

    return (
        <Popover
            overlayClassName="crm-region-selector-popover"
            placement="bottomLeft"
            trigger="click"
            visible={visible && !disabled}
            onVisibleChange={next => {
                if (disabled) {
                    return;
                }
                setVisible(next);
                if (!next) {
                    if (debounceTimerRef.current) {
                        window.clearTimeout(debounceTimerRef.current);
                    }
                    setKeyword('');
                    setSearchValue('');
                }
            }}
            content={overlayContent}
        >
            <div
                className={classname('crm-region-selector', className, {
                    disabled,
                    open: visible,
                    empty: selectedCities.length === 0,
                })}
                style={style}
                onClick={() => {
                    if (!disabled) {
                        setVisible(prev => !prev);
                    }
                }}
            >
                <div className="crm-region-selector-value">{renderTags()}</div>
                <div className="crm-region-selector-actions">
                    {selectedCities.length > 0 && !disabled ? (
                        <Icon
                            type="close-circle"
                            theme="filled"
                            className="crm-region-selector-action-icon"
                            onClick={(event: React.MouseEvent<HTMLElement>) => {
                                event.stopPropagation();
                                clearAll();
                            }}
                        />
                    ) : null}
                    <Icon
                        type={visible ? 'up' : 'down'}
                        className="crm-region-selector-action-icon arrow"
                    />
                </div>
            </div>
        </Popover>
    );
};

export default RegionSelector;
